import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { CATEGORY_GROUPS } from "../../lib/platformConstants";
import { useLang } from "../../context/LanguageContext";

/**
 * Genvägar in i förfrågningsformuläret för den som redan vet.
 *
 * **Det här är inte en katalog, och skillnaden är hela poängen.** Varje ruta går
 * till ``/boka`` med kategorin ifylld, aldrig till en lista över artister. Det
 * stående beslutet är att kunden inte ska bläddra: hon beskriver sitt event och
 * rätt personer hör av sig. En rad kort som leder till profiler hade motsagt det
 * på den enda sida där budskapet måste sitta.
 *
 * Priserna i designunderlaget, "Från 4 500 kr" och så vidare, är strukna. Vi har
 * ingen prisgolvsberäkning, och ett påhittat golv på startsidan blir ett
 * förväntansvärde som varje offert därefter mäts mot. Ska det stå ska det räknas
 * ur talangernas verkliga paketpriser, och det är en egen endpoint.
 *
 * Kategorierna kommer ur ``platformConstants``, samma källa som formuläret. En
 * kategori som läggs till där dyker upp här utan att någon rör den här filen.
 */

const T = {
  sv: { eyebrow: "Vet ni redan?", heading: "Gå direkt på rätt sorts talang.",
        sub: "Vi fyller i kategorin åt er. Resten tar två minuter." },
  en: { eyebrow: "Already know?", heading: "Go straight to the right kind of talent.",
        sub: "We fill in the category for you. The rest takes two minutes." },
};

// Bakgrundstoner per grupp. Dekor och inte information, så en grupp utan ton
// får den sista i listan i stället för att sakna bakgrund.
const TONES = [
  "from-orange-900/60", "from-purple-900/60", "from-blue-900/60",
  "from-emerald-900/60", "from-rose-900/60", "from-amber-900/60",
];

const CategoryShortcuts = () => {
  const { lang } = useLang();
  const t = T[lang === "sv" ? "sv" : "en"];
  const groups = CATEGORY_GROUPS.slice(0, 8);

  return (
    <section className="border-t border-[#1e1e1e]" data-testid="category-shortcuts">
      <div className="max-w-[1100px] mx-auto px-6 py-20 md:py-24">
        <div className="mb-10">
          <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-3">{t.eyebrow}</div>
          <h2 className="text-[30px] md:text-[38px] font-extrabold tracking-tight text-white">{t.heading}</h2>
          <p className="text-[14px] text-neutral-500 mt-2">{t.sub}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {groups.map((g, i) => (
            <Link key={g.id} to={`/boka?category_group=${g.id}`} data-testid={`cat-shortcut-${g.id}`}
              className="group rounded-xl border border-[#2A2A2A] bg-[#181818] overflow-hidden hover:border-[#FF5C00] transition-all hover:-translate-y-0.5">
              <div className={`aspect-[4/3] bg-gradient-to-br ${TONES[i % TONES.length]} via-neutral-800 to-black relative`}>
                <div className="absolute inset-0 flex flex-col justify-end p-4">
                  <div className="text-[19px] font-bold text-white">
                    {lang === "sv" ? (g.labelSv || g.label) : g.label}
                  </div>
                  <div className="flex items-center gap-1.5 text-[12px] text-neutral-400 mt-0.5 group-hover:text-[#FF7A2F] transition-colors">
                    {lang === "sv" ? "Skicka förfrågan" : "Send a request"}
                    <ArrowRight size={12} className="transition-transform group-hover:translate-x-0.5" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CategoryShortcuts;
